import React, { HTMLAttributes, useEffect, useState } from 'react'
import {
  autoUpdate,
  detectOverflow,
  Middleware,
  offset,
  Placement,
  shift,
  size,
  useFloating,
} from '@floating-ui/react-dom'

export type TDropdownPosition = 'auto' | 'top' | 'bottom'

export interface IDropdownPositionerProps extends HTMLAttributes<HTMLElement> {
  open?: boolean
  /** auto — приоритетно снизу; сверху, только если снизу не помещается минимум, а сверху места больше */
  position?: TDropdownPosition
  /** К какому краю якоря прижиматься */
  align?: 'start' | 'end'
  /** Своя ширина вместо ширины якоря (не шире окна); content — по содержимому */
  width?: number | 'content'
  /** Зазор между якорем и дропдауном */
  gap?: number
  /** Высота — место до края окна в этих границах; minHeight={Infinity} — не сжимается вовсе */
  minHeight?: number
  maxHeight?: number
}

/** Отступ дропдауна от краёв окна */
const VIEWPORT_PADDING = 8

const autoSide = (gap: number, minHeight: number): Middleware => ({
  name: 'autoSide',
  fn: ({ placement, rects }) => {
    const below =
      document.documentElement.clientHeight -
      rects.reference.y -
      rects.reference.height -
      gap -
      VIEWPORT_PADDING
    const above = rects.reference.y - gap - VIEWPORT_PADDING
    // высота дропдауна не бывает меньше minHeight, если он сам не короче: столько ему нужно снизу
    const needed = Math.min(rects.floating.height, minHeight)
    const side = below >= needed || below >= above ? 'bottom' : 'top'
    const [current, alignment] = placement.split('-')

    return current === side ? {} : { reset: { placement: `${side}-${alignment}` as Placement } }
  },
})

/** Якорь хотя бы частично ушёл из видимой области своего скролла или окна — дропдаун прячется */
const hideClipped: Middleware = {
  name: 'hideClipped',
  fn: async (state) => {
    const overflow = await detectOverflow(state, { elementContext: 'reference' })

    return { data: { clipped: Object.values(overflow).some((value) => value > 0) } }
  },
}

/**
 * Позиционирует дропдаун у якоря — ближайшего relative-предка (рамка поля, триггер меню).
 * Пропсы геометрии (position, align, width, gap, minHeight, maxHeight) задаются один раз:
 * floating-ui сравнивает middleware по тексту функций и новые значения не подхватит
 */
export const DropdownPositioner = ({
  children,
  open,
  position = 'bottom',
  align = 'start',
  width,
  gap = 6,
  minHeight = 200,
  maxHeight = 320,
}: IDropdownPositionerProps) => {
  const initialClassShowResult = 'opacity-0'
  const [classShowResult, setClassShowResult] = useState(initialClassShowResult)

  const { refs, floatingStyles, middlewareData } = useFloating<HTMLSpanElement>({
    strategy: 'fixed',
    placement: `${position === 'top' ? 'top' : 'bottom'}-${align}`,
    middleware: [
      offset(gap),
      position === 'auto' && autoSide(gap, minHeight),
      shift({ padding: VIEWPORT_PADDING }),
      size({
        padding: VIEWPORT_PADDING,
        // высоту по месту на экране берёт обёртка, сжимается в ней свой скролл содержимого
        apply: ({ rects, availableHeight, elements }) => {
          const viewportWidth = document.documentElement.clientWidth - VIEWPORT_PADDING * 2
          const height = Number.isFinite(minHeight)
            ? Math.min(maxHeight, Math.max(availableHeight, minHeight))
            : Infinity

          Object.assign(elements.floating.style, {
            width:
              width === 'content'
                ? ''
                : `${typeof width === 'number' ? Math.min(width, viewportWidth) : rects.reference.width}px`,
            maxHeight: Number.isFinite(height) ? `${height}px` : '',
          })
        },
      }),
      hideClipped,
    ],
    // за скроллом, ресайзом и сдвигами якоря следим, только пока дропдаун открыт
    whileElementsMounted: open ? autoUpdate : undefined,
  })

  useEffect(() => {
    open ? openDrop() : closeDrop()
  }, [open])

  const openDrop = () => setClassShowResult('opacity-100')
  const closeDrop = () => {
    setTimeout(() => setClassShowResult('opacity-0 invisible'), 150)
    setClassShowResult(initialClassShowResult)
  }

  return (
    <>
      {/* якорь: absolute inset-0 растягивается по ближайшему relative-предку */}
      <span ref={refs.setReference} className='invisible absolute inset-0' />
      {/* fixed отсчитывается от окна: overflow предков дропдаун не обрезает, хотя лежит рядом с якорем */}
      <div
        ref={refs.setFloating}
        style={{
          ...floatingStyles,
          visibility: middlewareData.hideClipped?.clipped ? 'hidden' : undefined,
        }}
        className={`z-10 flex flex-col transition-opacity duration-150 ${open ? 'pointer-events-auto' : 'pointer-events-none'} ${classShowResult} `}
      >
        {children}
      </div>
    </>
  )
}
